import CloseIcon from "@mui/icons-material/Close";
import {
  Button,
  IconButton,
  Step,
  StepLabel,
  Stepper,
  Typography,
} from "@mui/material";
import Box from "@mui/material/Box";
import Modal from "@mui/material/Modal";
import React from "react";
import logo from "../assets/logo_no_background.png";

const baseStyle = {
  position: "absolute" as "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: "100vw",
  maxWidth: 800,
  bgcolor: "#ffffff",
  border: "1px solid #e2e8f0",
  boxShadow: "0 10px 25px rgba(0,0,0,0.15)",
  borderRadius: 4,
  maxHeight: "90vh",
  overflow: "hidden",
};

export const useModal = () => {
  const [open, setOpen] = React.useState(false);

  const handleOpen = () => setOpen(true);
  const handleClose = () => setOpen(false);

  return {
    handleClose,
    handleOpen,
    open,
  };
};

interface ModalComponentProps {
  open: boolean;
  handleClose: () => void;
  sx?: any;
  title?: string;
  maxWidth?: number | string;
  autoHeight?: boolean;
  showClose?: boolean;
  children?: React.ReactNode;
  steps?: Array<{ label?: string; content: React.ReactNode }>;
  initialStep?: number;
  onComplete?: () => void;
}

export function ModalComponent({
  children,
  open,
  handleClose,
  sx = {},
  title,
  maxWidth,
  showClose = true,
  steps,
  initialStep = 0,
  onComplete,
  autoHeight,
}: ModalComponentProps) {
  const [activeStep, setActiveStep] = React.useState(initialStep);

  const isWizard = Array.isArray(steps) && steps.length > 0;

  const next = () => {
    if (!isWizard) return;
    if (activeStep === steps!.length - 1) {
      onComplete && onComplete();
      handleClose();
    } else {
      setActiveStep((s) => s + 1);
    }
  };

  const back = () => {
    if (!isWizard) return;
    setActiveStep((s) => Math.max(0, s - 1));
  };

  return (
    <>
      <Modal
        open={open}
        onClose={handleClose}
        BackdropProps={{
          sx: {
            backgroundColor: "rgba(36,50,77,0.35)",
            backdropFilter: "blur(2px)",
          },
        }}
      >
        <Box
          style={{
            overflowY: "hidden",
            padding: 0,
          }}
          sx={{
            ...baseStyle,
            maxWidth: maxWidth ?? baseStyle.maxWidth,
            overflowY: "hidden",
            width: { xs: "100%", sm: "100%" },

            ...(sx || {}),
          }}
        >
          {(title || showClose) && (
            <Box
              sx={{
                position: "relative",
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                p: { xs: 1.5, sm: 2.5 },
                m: 0,
                borderBottom: "1px solid #e2e8f0",
                backgroundColor: "#f8fafc",
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "row",
                  alignItems: "center",
                  gap: 2,
                }}
              >
                <img
                  src={logo}
                  alt="Praceria"
                  style={{
                    height: 64,
                    width: "auto",
                  }}
                />
                {title && (
                  <Typography
                    variant="h6"
                    sx={{
                      fontWeight: 600,
                      color: "#24324D",
                      textAlign: "left",
                    }}
                  >
                    {title}
                  </Typography>
                )}
              </Box>
              {showClose && (
                <IconButton
                  aria-label="Fechar"
                  onClick={handleClose}
                  sx={{
                    color: "#24324D",
                    position: "absolute",
                    right: { xs: 8, sm: 12 },
                    top: { xs: 8, sm: 12 },
                  }}
                >
                  <CloseIcon />
                </IconButton>
              )}
            </Box>
          )}

          <Box
            sx={{
              p: { xs: 2, sm: 3 },
              overflowY: "auto",
              height: autoHeight
                ? "auto"
                : { xs: "calc(90vh - 56px)", sm: "calc(85vh - 64px)" },
            }}
          >
            {isWizard ? (
              <>
                <Stepper activeStep={activeStep} sx={{ mb: { xs: 2, sm: 3 } }}>
                  {steps!.map((s, i) => (
                    <Step key={i}>
                      <StepLabel>{s.label}</StepLabel>
                    </Step>
                  ))}
                </Stepper>
                <Box sx={{ mb: { xs: 2, sm: 3 } }}>
                  {steps![activeStep]?.content}
                </Box>
                <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                  <Button
                    onClick={back}
                    disabled={activeStep === 0}
                    sx={{ color: "#24324D" }}
                  >
                    Voltar
                  </Button>
                  <Button
                    variant="contained"
                    onClick={next}
                    sx={{ backgroundColor: "#24324D" }}
                  >
                    {activeStep === steps!.length - 1
                      ? "Concluir"
                      : "Continuar"}
                  </Button>
                </Box>
              </>
            ) : (
              children
            )}
          </Box>
        </Box>
      </Modal>
    </>
  );
}
